import { memo } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { useSelector } from "react-redux";
import { getAccountState } from "../../../redux/slices/accountSlice";
import AdminSidebar from "./components/AdminSidebar";
import HomeScreen from "./screens/HomeScreen";
import CustomersScreen from "./screens/CustomersScreen";
import CustomerDetailScreen from "./screens/CustomerDetailScreen";
import EventsScreen from "./screens/EventsScreen";
import ReferralsScreen from "./screens/ReferralsScreen";
import InstagramScreen from "./screens/InstagramScreen";
import InstagramDmsScreen from "./screens/InstagramDmsScreen";
import PaymentsScreen from "./screens/PaymentsScreen";
import { colors, fontFamily, layout, type } from "./styles";

/**
 * Shell for the whole tool: the admin gate, the sidebar, and the nested routes.
 *
 * The `isAdmin` check here is UX only — it stops a non-admin from seeing a
 * broken page. The real access control is the backend's `requireAdmin`
 * middleware, which verifies the `admin` custom claim on every request.
 */
const MasterDashboardScreen = memo(function MasterDashboardScreenFn() {
  const { account } = useSelector(getAccountState);
  const { accountData, appInitialized } = account;

  if (!appInitialized) {
    return null;
  }

  if (!accountData.isAdmin) {
    return <Navigate to="/home" replace />;
  }

  return (
    <div style={{ fontFamily, background: colors.bg, minHeight: "100vh" }}>
      {/* This is a dense internal table-and-chart tool. Rather than reflow it
          into something unusable on a phone, say so — nobody triages customers
          from a phone, and a squeezed layout would only hide data. */}
      <div
        style={{
          display: "none",
          padding: 32,
          ...type.bodyRegular,
          color: colors.inkMuted,
        }}
        className="master-dashboard-narrow-note"
      >
        Open this dashboard on a wider screen.
      </div>

      <style>{`
        @media (max-width: ${layout.minWidth - 1}px) {
          .master-dashboard-shell { display: none !important; }
          .master-dashboard-narrow-note { display: block !important; }
        }
      `}</style>

      <div className="master-dashboard-shell" style={{ display: "flex", alignItems: "flex-start" }}>
        <AdminSidebar
          adminName={accountData.fullName}
          adminEmail={accountData.email}
        />

        <main
          style={{
            flex: 1,
            minWidth: 0,
            padding: layout.contentPadding,
            maxWidth: layout.contentMaxWidth,
          }}
        >
          <Routes>
            <Route index element={<Navigate to="home" replace />} />
            <Route path="home" element={<HomeScreen />} />
            <Route path="customers" element={<CustomersScreen />} />
            <Route path="customers/:uid" element={<CustomerDetailScreen />} />
            <Route path="events" element={<EventsScreen />} />
            <Route path="referrals" element={<ReferralsScreen />} />
            <Route path="instagram" element={<InstagramScreen />} />
            <Route path="instagram/dms" element={<InstagramDmsScreen />} />
            <Route path="payments" element={<PaymentsScreen />} />
            <Route path="*" element={<Navigate to="home" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
});

export default MasterDashboardScreen;
